import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { MAP, BASEMAP_STYLE, TYPE_COLOR, TYPE_LABEL } from './config.js'

let map
let handlers = {}
const markers = new Map() // id -> { marker, el, place }
let popup

export function initMap(onReady, callbacks = {}) {
  handlers = callbacks
  map = new maplibregl.Map({
    container: 'map',
    style: BASEMAP_STYLE,
    center: MAP.center,
    zoom: MAP.zoom,
    minZoom: MAP.minZoom,
    maxBounds: MAP.maxBounds,
    attributionControl: { compact: true },
  })
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right')
  // "Locate me" — live blue dot from the device GPS. Mobile-first: people use
  // this on the street. Needs HTTPS (Pages is) + the browser permission prompt.
  const geo = new maplibregl.GeolocateControl({
    positionOptions: { enableHighAccuracy: true },
    trackUserLocation: true,
    showUserLocation: true,
    showAccuracyCircle: true,
  })
  map.addControl(geo, 'top-right')
  geo.on('geolocate', (e) =>
    handlers.onGeolocate?.({ lat: e.coords.latitude, lng: e.coords.longitude }))
  popup = new maplibregl.Popup({ offset: 18, closeButton: true, maxWidth: '230px' })
  map.on('load', onReady)
  if (import.meta.env?.DEV) window.__map = map
}

// Render one custom HTML marker per place in the filtered set.
export function setMarkers(places) {
  for (const { marker } of markers.values()) marker.remove()
  markers.clear()
  popup.remove()

  for (const p of places) {
    const el = document.createElement('div')
    el.className = `pin ${p.locked ? 'locked' : ''} ${p.free ? 'top10' : ''}`
    if (!p.locked) el.style.background = TYPE_COLOR[p.type] || TYPE_COLOR.restaurant
    if (p.michelin) {
      el.classList.add('michelin')
      const star = document.createElement('span')
      star.className = 'pin-star'
      star.textContent = '★'
      star.title = `${p.michelin} Michelin star${p.michelin > 1 ? 's' : ''}`
      el.appendChild(star)
    }
    el.addEventListener('mouseenter', () => handlers.onHoverPin?.(p.id))
    el.addEventListener('mouseleave', () => handlers.onLeavePin?.(p.id))
    el.addEventListener('click', (e) => {
      e.stopPropagation()
      if (p.locked) return handlers.onLockedOpen?.(p.id)
      openPopup(p)
    })
    const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([p.lng, p.lat])
      .addTo(map)
    markers.set(p.id, { marker, el, place: p })
  }
}

function openPopup(p) {
  const node = document.createElement('div')
  const cover = p.image
    ? `<img class="popup-photo" src="${p.image}" alt="" loading="lazy" />` : ''
  node.innerHTML = `
    ${cover}
    <div class="popup-body">
      <div class="cat">${(p.cuisine || TYPE_LABEL[p.type] || '').toString()}</div>
      <h4>${p.name || ''}</h4>
      <button class="more">Read more</button>
    </div>`
  node.querySelector('.more').onclick = () => { popup.remove(); handlers.onOpen?.(p.id) }
  popup.setLngLat([p.lng, p.lat]).setDOMContent(node).addTo(map)
}

export function highlightPin(id, on) {
  markers.get(id)?.el.classList.toggle('hl', on)
}

export function fitToPlaces(places) {
  const pts = places.filter((p) => p.lat != null && p.lng != null)
  if (!pts.length) return
  if (pts.length === 1) {
    map.easeTo({ center: [pts[0].lng, pts[0].lat], zoom: 15 })
    return
  }
  const b = new maplibregl.LngLatBounds()
  for (const p of pts) b.extend([p.lng, p.lat])
  map.fitBounds(b, { padding: 70, maxZoom: 15.5, duration: 500 })
}

export function flyToPlace(p) {
  map.easeTo({ center: [p.lng, p.lat], zoom: Math.max(map.getZoom(), 15) })
}
